import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from "react-toastify";

import Header from '../../Header';
import Sidebar from '../../Sidebar';
import Footer from '../../Footer';
import Spinner from '../../common/Spinner';
import LazyCKEditor from '../../common/LazyCKEditor';
import { editBuild, updateBuildAccess, updateBuildVisibility } from '../../../redux/actions/BuildActions';
import { UserDropdown } from '../../../redux/actions/userActions';
import { BUILD_EDIT_RESET } from "../../../redux/constants/BuildConstants";

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import LinkIcon from '@mui/icons-material/Link';
import EditIcon from '@mui/icons-material/Edit';

const DetailRow = ({ label, children }) => (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ py: 0.75 }}>
        <Typography
            variant="body2"
            sx={{ color: 'text.secondary', minWidth: 140, fontWeight: 600 }}
        >
            {label}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Stack>
);

const BuildDetails = ({ match }) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const appId = match.params.id;
    const buildId = match.params.buildId;
    const _organizationId = match?.params?.organizationId;

    const buildEdit = useSelector((state) => state.buildEdit);
    const users = useSelector((state) => state.userDropdown.users);
    const userRole = useSelector((state) => state.userLogin.userInfo.data.role);
    const organization = useSelector((state) => state?.organizationDetails?.organization);
    const { build } = buildEdit;
    const role = organization?.user_role;
    const organizationId = organization?.organization_id;

    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [userIds, setUserIds] = useState([]);
    const [selectedClientIds, setSelectedClientIds] = useState([]);
    const [accessEdit, setAccessEdit] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isPublic, setIsPublic] = useState(false);
    const [savingVisibility, setSavingVisibility] = useState(false);

    const navigateToBuildList = () => {
        dispatch({ type: BUILD_EDIT_RESET });
        if (_organizationId) {
            history.push(`/organization/${_organizationId}/app/${appId}/builds`);
        } else {
            history.push(`/app/${appId}/builds`);
        }
    };

    useEffect(() => {
        if (build?.id !== Number(buildId)) {
            dispatch(editBuild(appId, buildId)).then((response) => {
                if (response === 'Build not found.') {
                    navigateToBuildList();
                }
            });
        } else {
            setSelectedUserIds(build?.build_access);
            setUserIds(build?.build_access);
            setIsPublic(!!build?.is_public);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [build, dispatch, buildId]);

    useEffect(() => {
        if (role === "Admin" || role === "Member") {
            dispatch(UserDropdown('build', appId, organizationId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId]);

    const handleVisibilityToggle = async () => {
        const next = !isPublic;
        setSavingVisibility(true);
        setIsPublic(next);
        const res = await dispatch(updateBuildVisibility(appId, buildId, next));
        setSavingVisibility(false);
        if (!res || res.success === 0) {
            setIsPublic(!next);
        }
    };

    const copyShareUrl = () => {
        const shareUrl = `${window.location.origin}/install/${appId}/${buildId}`;
        navigator.clipboard
            .writeText(shareUrl)
            .then(() => toast.success("Public install URL copied"))
            .catch(() => toast.error("Failed to copy URL"));
    };

    const handleUserChange = (event) => {
        const selectedUserId = parseInt(event.target.value);
        if (!selectedUserIds.includes(selectedUserId)) {
            setSelectedUserIds([...selectedUserIds, selectedUserId]);
        }
    };

    const removeUser = (userIdToRemove) => {
        setSelectedUserIds(selectedUserIds.filter((id) => id !== userIdToRemove));
    };

    const handleClientChange = (event) => {
        const selectedClientId = parseInt(event.target.value);
        if (!selectedClientIds.includes(selectedClientId)) {
            setSelectedClientIds([...selectedClientIds, selectedClientId]);
        }
    };

    const removeClient = (clientIdToRemove) => {
        setSelectedClientIds(selectedClientIds.filter((id) => id !== clientIdToRemove));
    };

    const filteredClientOptions = users.filter(
        (option) => !selectedClientIds.includes(option.id) && option.role === 'Client'
    );
    const selectedClients = users.filter(
        (option) => selectedClientIds.includes(option.id) && option.role === 'Client'
    );
    const clients = users.filter((option) => option.role === 'Client');

    let filteredOptions = [];
    let selectedUsers = [];
    if (role === "Admin" || role === "Member") {
        filteredOptions = users.filter((option) => !selectedUserIds.includes(option.id));
        selectedUsers = users.filter((option) => selectedUserIds.includes(option.id));
    }

    const downloadBuild = () => {
        const link = document.createElement('a');
        link.href = build?.file_path;
        link.download = `app -${build?.version}`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const removeIds = userIds.filter((element) => !selectedClientIds.includes(element));
        setLoading(true);
        const obj = {
            selectedUserIds: selectedClientIds,
            removeIds,
            os: build?.os,
            version: build?.version,
        };
        dispatch(updateBuildAccess(obj, appId, buildId)).then(() => {
            setLoading(false);
            navigateToBuildList();
        });
    };

    const canEditClientAccess =
        (role === "Admin" || role === "Member") && userRole !== "Super_Admin";

    return (
        <>
            <div className="container-scroller">
                <Header />
                <div className="container-fluid page-body-wrapper">
                    <Sidebar />
                    <Box component="main" className="main-panel" sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flex: 1, py: { xs: 2, md: 3 } }}>
                            <Container maxWidth="lg" disableGutters sx={{ px: { xs: 2, md: 3 } }}>
                                <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider' }}>
                                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                                        <IconButton onClick={navigateToBuildList} size="small" aria-label="Back">
                                            <ArrowBackIcon />
                                        </IconButton>
                                        <Typography variant="h3" component="h1" sx={{ flex: 1, minWidth: 0 }} noWrap>
                                            Build details
                                        </Typography>
                                    </Stack>

                                    <Box component="form" onSubmit={handleSubmit}>
                                        <DetailRow label="OS">
                                            <Typography variant="body1">{build?.os || '—'}</Typography>
                                        </DetailRow>
                                        <DetailRow label="Version">
                                            <Typography variant="body1">{build?.version || '—'}</Typography>
                                        </DetailRow>
                                        <DetailRow label="Environment">
                                            <Typography variant="body1">
                                                {build?.envirionment || build?.environment || '—'}
                                            </Typography>
                                        </DetailRow>
                                        <DetailRow label="Build file">
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<DownloadIcon />}
                                                onClick={downloadBuild}
                                                disabled={!build?.file_path}
                                            >
                                                Download
                                            </Button>
                                        </DetailRow>

                                        <DetailRow label="Visibility">
                                            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                                <Chip
                                                    label={isPublic ? 'Public' : 'Private'}
                                                    color={isPublic ? 'success' : 'default'}
                                                    size="small"
                                                    sx={{ fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}
                                                />
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={handleVisibilityToggle}
                                                    disabled={savingVisibility}
                                                >
                                                    {savingVisibility
                                                        ? 'Saving…'
                                                        : isPublic
                                                            ? 'Make Private'
                                                            : 'Make Public'}
                                                </Button>
                                                {isPublic && (
                                                    <Button
                                                        variant="text"
                                                        size="small"
                                                        startIcon={<LinkIcon />}
                                                        onClick={copyShareUrl}
                                                    >
                                                        Copy install link
                                                    </Button>
                                                )}
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {isPublic
                                                        ? 'Anyone with the install URL can install this build.'
                                                        : 'Only organisation members can install this build.'}
                                                </Typography>
                                            </Stack>
                                        </DetailRow>

                                        <Divider sx={{ my: 2 }} />

                                        <Box sx={{ mb: 2 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: 'text.secondary', fontWeight: 600, mb: 1 }}
                                            >
                                                Release notes
                                            </Typography>
                                            <Box
                                                sx={{
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderRadius: 1,
                                                    p: 1.5,
                                                    bgcolor: 'background.default',
                                                }}
                                            >
                                                <LazyCKEditor
                                                    data={build?.notes || ''}
                                                    disabled
                                                    config={{ toolbar: [], isReadOnly: true }}
                                                />
                                            </Box>
                                        </Box>

                                        {canEditClientAccess && (
                                            <>
                                                <Divider sx={{ my: 2 }} />
                                                <Box sx={{ mb: 2 }}>
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        sx={{ mb: 1 }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ color: 'text.secondary', fontWeight: 600 }}
                                                        >
                                                            Client access
                                                        </Typography>
                                                        {!accessEdit && (
                                                            <Button
                                                                size="small"
                                                                startIcon={<EditIcon />}
                                                                onClick={() => setAccessEdit(true)}
                                                            >
                                                                Edit access
                                                            </Button>
                                                        )}
                                                    </Stack>

                                                    {accessEdit ? (
                                                        <>
                                                            {selectedUsers.length > 0 && (
                                                                <Stack
                                                                    direction="row"
                                                                    spacing={1}
                                                                    flexWrap="wrap"
                                                                    useFlexGap
                                                                    sx={{ mb: 1.5 }}
                                                                >
                                                                    {selectedUsers.map((u) => (
                                                                        <Chip
                                                                            key={u.id}
                                                                            label={u.name}
                                                                            onDelete={() => removeUser(u.id)}
                                                                            size="small"
                                                                        />
                                                                    ))}
                                                                </Stack>
                                                            )}
                                                            <FormControl fullWidth size="small">
                                                                <InputLabel>Add user</InputLabel>
                                                                <Select
                                                                    label="Add user"
                                                                    value=""
                                                                    onChange={handleUserChange}
                                                                >
                                                                    {filteredOptions.map((u) => (
                                                                        <MenuItem key={u.id} value={u.id}>
                                                                            {u.name}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                                                                <Button type="submit" variant="contained">
                                                                    Save access
                                                                </Button>
                                                                <Button
                                                                    variant="outlined"
                                                                    onClick={() => setAccessEdit(false)}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                            </Stack>
                                                        </>
                                                    ) : clients.length > 0 ? (
                                                        <>
                                                            {selectedClients.length > 0 && (
                                                                <Stack
                                                                    direction="row"
                                                                    spacing={1}
                                                                    flexWrap="wrap"
                                                                    useFlexGap
                                                                    sx={{ mb: 1.5 }}
                                                                >
                                                                    {selectedClients.map((u) => (
                                                                        <Chip
                                                                            key={u.id}
                                                                            label={u.name}
                                                                            onDelete={() => removeClient(u.id)}
                                                                            size="small"
                                                                        />
                                                                    ))}
                                                                </Stack>
                                                            )}
                                                            <FormControl fullWidth size="small">
                                                                <InputLabel>Add client</InputLabel>
                                                                <Select
                                                                    label="Add client"
                                                                    value=""
                                                                    onChange={handleClientChange}
                                                                >
                                                                    {filteredClientOptions.map((u) => (
                                                                        <MenuItem key={u.id} value={u.id}>
                                                                            {u.name}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                            {selectedClients.length > 0 && role !== 'Client' && (
                                                                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                                                                    <Button type="submit" variant="contained">
                                                                        Save
                                                                    </Button>
                                                                    <Button
                                                                        variant="outlined"
                                                                        onClick={navigateToBuildList}
                                                                    >
                                                                        Cancel
                                                                    </Button>
                                                                </Stack>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                            Contact your admin to add a client.
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </>
                                        )}
                                    </Box>
                                </Paper>
                            </Container>
                        </Box>
                        <Footer />
                    </Box>
                </div>
            </div>
            {loading && <Spinner fullscreen label="Saving…" />}
        </>
    );
};

export default BuildDetails;
